import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  LayoutDashboard,
  Inbox,
  FileText,
  Shield,
  Users,
  Settings,
  Bell,
  ChevronLeft,
  ChevronRight,
  LogOut,
  User,
  Building2,
  Scale,
  Eye,
  Menu,
  Brain,
  AlertTriangle,
  Zap,
  MessageSquare,
  Clock,
  Plug,
  FileWarning,
  ShieldCheck,
  BarChart3,
  Globe,
  Printer,
  Search,
  HeartPulse,
  HelpCircle,
  Sparkles
} from 'lucide-react';
import { GlobalSearch } from '../components/GlobalSearch';

const roleLabels = {
  ADMIN_ESTADUAL: 'Admin Estadual',
  CASA_CIVIL: 'Casa Civil',
  CGE: 'CGE',
  SECRETARIA_EXECUTORA: 'Secretaria Executora',
  UNIDADE_INTERNA: 'Unidade Interna',
  JURIDICO: 'Jurídico',
  GOVERNADOR: 'Governador',
  OBSERVADOR_EXTERNO: 'Observador'
};

const roleIcons = {
  ADMIN_ESTADUAL: Shield,
  CASA_CIVIL: Building2,
  CGE: Eye,
  SECRETARIA_EXECUTORA: FileText,
  UNIDADE_INTERNA: Users,
  JURIDICO: Scale,
  GOVERNADOR: User,
  OBSERVADOR_EXTERNO: Eye
};

const navigationGroups = [
  {
    label: 'Visão Geral',
    items: [
      { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard, permission: 'view' },
      { name: 'Meus Atos', href: '/inbox', icon: Inbox, permission: 'view' },
    ],
  },
  {
    label: 'Gestão de Atos',
    items: [
      { name: 'Atos de Controle', href: '/acts', icon: FileText, permission: 'view' },
      { name: 'TCE', href: '/tce', icon: FileWarning, permission: 'view', roles: ['ADMIN_ESTADUAL', 'CGE', 'CONTROLE_INTERNO', 'AUDITORIA', 'JURIDICO'] },
      { name: 'Casa Civil', href: '/dashboard/casa-civil', icon: Building2, permission: 'view', roles: ['CASA_CIVIL', 'ADMIN_ESTADUAL'] },
      { name: 'CGE', href: '/dashboard/cge', icon: Eye, permission: 'view', roles: ['CGE', 'ADMIN_ESTADUAL', 'CONTROLE_INTERNO'] },
    ],
  },
  {
    label: 'Compliance & Riscos',
    items: [
      { name: 'Integridade', href: '/integridade', icon: Shield, permission: 'view' },
      { name: 'LGPD', href: '/compliance/lgpd', icon: ShieldCheck, permission: 'view' },
      { name: 'Governança', href: '/governanca', icon: BarChart3, permission: 'view' },
      { name: 'Conformidade', href: '/transparencia', icon: Globe, permission: 'view' },
      { name: 'Alertas', href: '/alerts', icon: AlertTriangle, permission: 'view' },
    ],
  },
  {
    label: 'Inteligência & Relatórios',
    items: [
      { name: 'AI Engine', href: '/ai-engine', icon: Brain, permission: 'view' },
      { name: 'Relatórios', href: '/relatorios', icon: Printer, permission: 'view' },
      { name: 'Auditoria', href: '/audit', icon: FileText, permission: 'audit' },
    ],
  },
  {
    label: 'Administração',
    items: [
      { name: 'Notificações', href: '/notifications', icon: Bell, permission: 'view' },
      { name: 'Status do Sistema', href: '/health', icon: HeartPulse, permission: 'view' },
      { name: 'Integrações', href: '/integrations', icon: Plug, permission: 'view', roles: ['ADMIN_ESTADUAL', 'GESTOR_GERAL'] },
      { name: 'Usuários', href: '/users', icon: Users, permission: 'manage_users' },
      { name: 'Configurações', href: '/settings', icon: Settings, permission: 'view' },
    ],
  },
];

// Manter array plano para retrocompatibilidade (breadcrumb do header)
const navigation = navigationGroups.flatMap(g => g.items);

// Admin navigation - only shown to specific roles
const adminNavigation = [
  { name: 'Admin Governo', href: '/admin/governo', icon: Building2, 
    allowedRoles: ['ADMIN_ESTADUAL', 'GESTOR_GERAL', 'TI_INSTITUCIONAL', 'CONTROLADORIA_INTERNA', 'PROCURADORIA_JURIDICO'] },
];

export const AppLayout = ({ children }) => {
  const { user, logout, hasPermission, api } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [showMessagesDropdown, setShowMessagesDropdown] = useState(false);
  const [recentMessages, setRecentMessages] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [showNotificationsDropdown, setShowNotificationsDropdown] = useState(false);
  const [notificationCount, setNotificationCount] = useState(0);
  const [searchOpen, setSearchOpen] = useState(false);

  // Fetch unread messages count
  useEffect(() => {
    const fetchUnreadMessages = async () => {
      if (!user) return;
      try {
        const res = await api.get('/notifications/unread-messages');
        setUnreadMessages(res.data.count || 0);
        setRecentMessages(res.data.messages || []);
      } catch (e) {
        // Silently fail
      }
    };
    
    fetchUnreadMessages();
    const interval = setInterval(fetchUnreadMessages, 30000); // Check every 30s
    return () => clearInterval(interval);
  }, [user, api]);

  // Fetch all notifications (deadlines, alerts, system)
  useEffect(() => {
    const fetchNotifications = async () => {
      if (!user) return;
      try {
        const res = await api.get('/notifications/all');
        setNotifications(res.data.notifications || []);
        setNotificationCount(res.data.count || 0);
      } catch (e) {
        // Silently fail
      }
    };
    
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60000); // Check every 60s
    return () => clearInterval(interval);
  }, [user, api]);

  const RoleIcon = roleIcons[user?.role] || User;

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const filteredNavigation = navigation.filter(item => {
    // Check permission first
    if (!hasPermission(item.permission)) return false;
    // If item has role restriction, check it
    if (item.roles && user?.role && !item.roles.includes(user.role)) return false;
    return true;
  });

  // Agrupamento filtrado por permissões/role (mantém ordem original dos grupos)
  const filteredGroups = navigationGroups
    .map(group => ({
      ...group,
      items: group.items.filter(item => {
        if (!hasPermission(item.permission)) return false;
        if (item.roles && user?.role && !item.roles.includes(user.role)) return false;
        return true;
      }),
    }))
    .filter(group => group.items.length > 0);
  
  // Filter admin navigation based on user role
  const filteredAdminNavigation = adminNavigation.filter(item => 
    user?.role && item.allowedRoles.includes(user.role)
  );

  return (
    <div className="app-layout flex">
      {/* Mobile menu button */}
      <Button
        variant="ghost"
        size="icon"
        className="fixed top-4 left-4 z-50 lg:hidden"
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        data-testid="mobile-menu-btn"
      >
        <Menu className="h-5 w-5" />
      </Button>

      {/* Sidebar */}
      <aside
        className={`sidebar sidebar-transition ${collapsed ? 'w-16' : 'w-64'} ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}
        data-testid="sidebar"
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className={`p-4 border-b border-slate-200 ${collapsed ? 'px-3' : ''}`}>
            <Link to="/dashboard" className="flex items-center gap-3" data-testid="logo-link">
              <img 
                src="/bluegov-logo.png" 
                alt="BlueGov" 
                className="w-10 h-10 rounded-xl flex-shrink-0 object-contain"
              />
              {!collapsed && (
                <div>
                  <h1 className="text-lg font-bold text-slate-900">BlueGov</h1>
                  <p className="text-xs text-slate-500">Gestão de Atos</p>
                </div>
              )}
            </Link>
          </div>

          {/* Navigation — agrupada por seções */}
          <nav className="flex-1 p-3 space-y-4 overflow-y-auto" data-testid="sidebar-nav">
            {filteredGroups.map((group) => (
              <div key={group.label} data-testid={`nav-group-${group.label.toLowerCase().replace(/\s/g, '-')}`}>
                {!collapsed && (
                  <p className="px-3 mb-1.5 text-[10px] font-semibold tracking-wider text-slate-400 uppercase">
                    {group.label}
                  </p>
                )}
                <div className="space-y-1">
                  {group.items.map((item) => {
                    const isActive = location.pathname === item.href || location.pathname.startsWith(item.href + '/');
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-all ${
                          isActive
                            ? 'nav-item-active'
                            : 'text-slate-600 hover:bg-slate-100'
                        } ${collapsed ? 'justify-center' : ''}`}
                        data-testid={`nav-${item.name.toLowerCase().replace(/\s/g, '-')}`}
                        onClick={() => setMobileMenuOpen(false)}
                        title={collapsed ? item.name : undefined}
                      >
                        <item.icon className={`w-[18px] h-[18px] flex-shrink-0 ${isActive ? 'text-[#1d4ed8]' : ''}`} />
                        {!collapsed && <span className="text-sm">{item.name}</span>}
                      </Link>
                    );
                  })}
                </div>
              </div>
            ))}
            
            {/* Admin Navigation - Only for authorized roles */}
            {filteredAdminNavigation.length > 0 && (
              <div data-testid="nav-group-admin-governo">
                {!collapsed && (
                  <p className="px-3 mb-1.5 text-[10px] font-semibold tracking-wider text-slate-400 uppercase">
                    Admin Avançado
                  </p>
                )}
                <div className="space-y-1">
                  {filteredAdminNavigation.map((item) => {
                    const isActive = location.pathname === item.href || location.pathname.startsWith(item.href + '/');
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-all ${
                          isActive
                            ? 'bg-blue-50 text-blue-700 font-medium'
                            : 'text-slate-600 hover:bg-slate-100'
                        } ${collapsed ? 'justify-center' : ''}`}
                        data-testid={`nav-${item.name.toLowerCase().replace(/\s/g, '-')}`}
                        onClick={() => setMobileMenuOpen(false)}
                        title={collapsed ? item.name : undefined}
                      >
                        <item.icon className={`w-[18px] h-[18px] flex-shrink-0 ${isActive ? 'text-blue-600' : ''}`} />
                        {!collapsed && <span className="text-sm">{item.name}</span>}
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}
          </nav>

          {/* User section */}
          <div className={`p-3 border-t border-slate-200 ${collapsed ? 'px-2' : ''}`}>
            {!collapsed && user && (
              <div className="px-3 py-2 mb-2 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-2 text-xs text-slate-500">
                  <RoleIcon className="w-3.5 h-3.5" />
                  <span>{roleLabels[user.role]}</span>
                </div>
                <p className="text-xs font-mono text-slate-400 mt-1 truncate">{user.tenant_id}</p>
              </div>
            )}
            <Button
              variant="ghost"
              size="sm"
              className={`w-full ${collapsed ? 'px-0' : ''}`}
              onClick={() => setCollapsed(!collapsed)}
              data-testid="collapse-sidebar-btn"
            >
              {collapsed ? <ChevronRight className="w-4 h-4" /> : <><ChevronLeft className="w-4 h-4 mr-2" /> Recolher</>}
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile overlay */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Main content */}
      <div className={`main-content flex-1 ${collapsed ? 'lg:ml-16' : 'lg:ml-64'}`}>
        {/* Header */}
        <header className="app-header px-4 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="lg:hidden w-10" /> {/* Spacer for mobile */}
            <div className="hidden lg:block">
              <h2 className="text-xl font-semibold text-slate-900">
                {filteredNavigation.find(n => location.pathname.startsWith(n.href))?.name || 'BlueGov'}
              </h2>
            </div>

            <div className="flex items-center gap-2 lg:gap-3">
              {/* Global Search Button */}
              <button
                onClick={() => setSearchOpen(true)}
                data-testid="global-search-btn"
                className="hidden md:flex items-center gap-2 px-3 py-1.5 border border-slate-200 rounded-lg text-sm text-slate-500 hover:border-slate-300 hover:bg-slate-50 transition-colors"
              >
                <Search className="w-4 h-4" />
                <span>Buscar...</span>
              </button>
              <button
                onClick={() => setSearchOpen(true)}
                data-testid="global-search-btn-mobile"
                className="md:hidden p-2 rounded-lg hover:bg-slate-100"
                aria-label="Buscar"
              >
                <Search className="w-5 h-5 text-slate-600" />
              </button>

              {/* Messages Notifications */}
              <DropdownMenu open={showMessagesDropdown} onOpenChange={setShowMessagesDropdown}>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="relative" data-testid="messages-btn">
                    <MessageSquare className="w-5 h-5 text-slate-600" />
                    {unreadMessages > 0 && (
                      <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-medium">
                        {unreadMessages > 9 ? '9+' : unreadMessages}
                      </span>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-80">
                  <div className="p-3 border-b">
                    <h4 className="font-semibold text-sm">Mensagens</h4>
                    <p className="text-xs text-slate-500">{unreadMessages} não lidas</p>
                  </div>
                  {recentMessages.length > 0 ? (
                    <div className="max-h-64 overflow-y-auto">
                      {recentMessages.slice(0, 5).map((msg, i) => (
                        <DropdownMenuItem key={i} className="p-3 cursor-pointer" onClick={() => navigate('/admin/governo')}>
                          <div className="flex items-start gap-3 w-full">
                            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                              <MessageSquare className="w-4 h-4 text-blue-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">{msg.sender_name}</p>
                              <p className="text-xs text-slate-500 truncate">{msg.message}</p>
                              <p className="text-xs text-slate-400 mt-1">
                                {new Date(msg.created_at).toLocaleString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                              </p>
                            </div>
                            {!msg.read && <div className="w-2 h-2 bg-blue-500 rounded-full" />}
                          </div>
                        </DropdownMenuItem>
                      ))}
                    </div>
                  ) : (
                    <div className="p-6 text-center text-slate-400">
                      <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">Nenhuma mensagem</p>
                    </div>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className="p-3 justify-center text-blue-600 font-medium" onClick={() => navigate('/admin/governo')}>
                    Ver todas as mensagens
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* General Notifications */}
              <DropdownMenu open={showNotificationsDropdown} onOpenChange={setShowNotificationsDropdown}>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="relative" data-testid="notifications-btn">
                    <Bell className="w-5 h-5 text-slate-600" />
                    {notificationCount > 0 && (
                      <span className="absolute -top-1 -right-1 w-5 h-5 bg-orange-500 text-white text-xs rounded-full flex items-center justify-center font-medium animate-pulse">
                        {notificationCount > 9 ? '9+' : notificationCount}
                      </span>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-96">
                  <div className="p-3 border-b bg-gradient-to-r from-slate-50 to-white">
                    <h4 className="font-semibold text-sm">Notificações</h4>
                    <p className="text-xs text-slate-500">{notificationCount} pendentes</p>
                  </div>
                  {notifications.length > 0 ? (
                    <div className="max-h-80 overflow-y-auto">
                      {notifications.slice(0, 8).map((notif, i) => (
                        <DropdownMenuItem 
                          key={i} 
                          className="p-3 cursor-pointer hover:bg-slate-50" 
                          onClick={() => {
                            if (notif.link) navigate(notif.link);
                            setShowNotificationsDropdown(false);
                          }}
                        >
                          <div className="flex items-start gap-3 w-full">
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                              notif.type === 'DEADLINE' ? 'bg-red-100' :
                              notif.type === 'ALERT' ? 'bg-amber-100' :
                              notif.type === 'MESSAGE' ? 'bg-blue-100' :
                              'bg-slate-100'
                            }`}>
                              {notif.type === 'DEADLINE' ? <Clock className={`w-5 h-5 text-red-600`} /> :
                               notif.type === 'ALERT' ? <AlertTriangle className={`w-5 h-5 text-amber-600`} /> :
                               notif.type === 'MESSAGE' ? <MessageSquare className={`w-5 h-5 text-blue-600`} /> :
                               <Bell className={`w-5 h-5 text-slate-600`} />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="text-sm font-medium truncate">{notif.title}</p>
                                {notif.priority === 'HIGH' && (
                                  <Badge className="bg-red-100 text-red-700 text-[10px] px-1.5">Urgente</Badge>
                                )}
                              </div>
                              <p className="text-xs text-slate-500 line-clamp-2 mt-0.5">{notif.message}</p>
                              <p className="text-xs text-slate-400 mt-1">
                                {new Date(notif.created_at).toLocaleString('pt-BR', { 
                                  day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' 
                                })}
                              </p>
                            </div>
                            {!notif.read && <div className="w-2 h-2 bg-orange-500 rounded-full flex-shrink-0 mt-2" />}
                          </div>
                        </DropdownMenuItem>
                      ))}
                    </div>
                  ) : (
                    <div className="p-8 text-center text-slate-400">
                      <Bell className="w-10 h-10 mx-auto mb-3 opacity-30" />
                      <p className="text-sm font-medium">Tudo em dia!</p>
                      <p className="text-xs mt-1">Nenhuma notificação pendente</p>
                    </div>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    className="p-3 justify-center text-blue-600 font-medium" 
                    onClick={() => { navigate('/notifications'); setShowNotificationsDropdown(false); }}
                    data-testid="see-all-notifications-link"
                  >
                    Abrir central de notificações
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* User menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className="flex items-center gap-2 px-2 text-slate-700 hover:bg-slate-100 hover:text-slate-900 focus:text-slate-900 focus-visible:text-slate-900"
                    data-testid="user-menu-btn"
                  >
                    <Avatar className="w-8 h-8">
                      <AvatarImage src={user?.picture} />
                      <AvatarFallback className="bg-[#1d4ed8] text-white text-sm">
                        {user?.name?.charAt(0) || 'U'}
                      </AvatarFallback>
                    </Avatar>
                    <span className="hidden md:inline text-sm font-medium">
                      {user?.name}
                    </span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-2 py-2">
                    <p className="text-sm font-medium text-slate-900">{user?.name}</p>
                    <p className="text-xs text-slate-500">{user?.email}</p>
                    <Badge variant="secondary" className="mt-2 text-xs">
                      {roleLabels[user?.role]}
                    </Badge>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="settings-menu-item">
                    <Settings className="w-4 h-4 mr-2" />
                    Configurações
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="logout-menu-item">
                    <LogOut className="w-4 h-4 mr-2" />
                    Sair
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 lg:p-8">
          {children}
        </main>
      </div>

      {/* Busca Global (⌘K) */}
      <GlobalSearch open={searchOpen} setOpen={setSearchOpen} />
    </div>
  );
};

export default AppLayout;
